import { createClient } from '@/prismicio';
import { Content, ContentRelationshipField } from '@prismicio/client';
import { SliceComponentProps } from '@prismicio/react';
import { FormDocument } from '../../../prismicio-types';
import { getFormById } from '@/actions';
import { PrismicNextLink } from '@prismicio/next';
import { FormLayout } from '@/components';
import { GoogleReCaptchaContext } from '@/context';
import { ArrowRightIcon } from '@/assets';
import dynamic from 'next/dynamic';

const StarfieldBackground = dynamic(
  () => import('@/components/StarfieldBackground'),
  { ssr: false },
);

/**
 * Props for `Form`.
 */
export type FormProps = SliceComponentProps<Content.FormSlice> & {
  context: { hubspotPortalIdForm: string };
};

/**
 * Component for "Form" Slices.
 */
const Form = async ({ slice, context }: FormProps) => {
  const {
    primary: { form, title, bullet_points, button_cta, button_link },
  } = slice;

  const typedRelationshipFieldForm = form as ContentRelationshipField &
    FormDocument;

  const client = createClient();
  const formPrismic = await client.getByID<FormDocument>(
    typedRelationshipFieldForm.id,
  );

  const {
    data: {
      title: formTitle,
      description,
      form_id: formId,
      utm_ebook: utmBook,
    },
  } = formPrismic;

  const hubspotFormData = await getFormById(formId);

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="relative text-white overflow-hidden min-h-screen pt-32 pb-24 md:pt-48 md:pb-40 flex items-center"
    >
      {/* Starfield background */}
      <StarfieldBackground />

      <div className="container mx-auto px-6 py-16">
        <div className="grid md:grid-cols-2 gap-16 items-center w-full">
          <div className="text-white">
            {title && <h2 className="text-5xl font-bold mb-4">{title}</h2>}
            {bullet_points && bullet_points.length > 0 && (
              <ul className="list-none space-y-2 mb-6">
                {bullet_points.map((item, index) => (
                  <li key={index} className="flex items-center">
                    <span className="text-green-400 mr-2">&#10003;</span>
                    {item.bullet_point}
                  </li>
                ))}
              </ul>
            )}
            {button_link && button_cta && (
              <PrismicNextLink field={button_link} className="secondary-button">
                <ArrowRightIcon className="arrow-icon" />
                {button_cta}
              </PrismicNextLink>
            )}
          </div>
          <div
            className="w-full p-8 md:p-12 rounded-2xl text-white [&_h1]:text-white [&_h2]:text-white [&_h3]:text-white [&_h4]:text-white [&_h5]:text-white [&_h6]:text-white [&_*]:text-white"
            style={{
              borderRadius: '16px',
              background:
                'linear-gradient(180deg, rgba(0, 0, 0, 0.20) 0%, rgba(0, 0, 0, 0.20) 100%)',
              boxShadow: '0 4.325px 64.871px 0 rgba(0, 0, 0, 0.08)',
              backdropFilter: 'blur(10px)',
            }}
          >
            <GoogleReCaptchaContext>
              <FormLayout
                formTitle={formTitle}
                formDescription={description}
                formData={hubspotFormData}
                hubspotPortalIdForm={context.hubspotPortalIdForm}
                hubspotGuidForm={formId as string}
                utmEbook={utmBook as string}
              />
            </GoogleReCaptchaContext>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Form;
